enum KwargType {
  INTEGER = 'integer',
  FLOAT = 'float',
  LIST_OF_FLOAT = 'listOfFloat',
  BOOLEAN = 'boolean',
  REDUCTION_TYPES = 'reductionTypes', // none, mean, sum (for loss functions)
}

const kwargNames = {
  [KwargType.INTEGER]: 'Integer',
  [KwargType.FLOAT]: 'Float',
  [KwargType.LIST_OF_FLOAT]: 'List of floats',
  [KwargType.BOOLEAN]: 'Boolean',
  [KwargType.REDUCTION_TYPES]: 'Reduction types',
}

abstract class Kwarg {
  public name: string = $state('')
  #type: KwargType = $state(KwargType.INTEGER)
  abstract value: number | number[] | string | boolean

  constructor(name: string, type: KwargType) {
    this.name = name
    this.#type = type
  }

  get type() {
    return this.#type
  }

  toJSON() {
    return {
      name: this.name,
      value: this.value,
      type: this.type,
    }
  }
}

class KwargError extends Error {
  constructor(
    message: string,
    public kwarg: Kwarg,
  ) {
    super(message)
  }
}

class IntKwarg extends Kwarg {
  public value: number = $state(0)

  constructor(name: string, value: number) {
    super(name, KwargType.INTEGER)
    this.value = value
  }

  static prepare(value: number | number[] | string | boolean): number {
    if (Array.isArray(value)) {
      return value[0]
    } else {
      if (typeof value === 'number') {
        return value
      } else if (typeof value === 'boolean') {
        return value ? 1 : 0
      } else {
        return parseInt(value)
      }
    }
  }

  static fromOtherKwarg(otherKwarg: Kwarg): IntKwarg {
    return new IntKwarg(otherKwarg.name, this.prepare(otherKwarg.value))
  }
}

class FloatKwarg extends Kwarg {
  public value: number = $state(0)

  constructor(name: string, value: number) {
    super(name, KwargType.FLOAT)
    this.value = value
  }

  static prepare(value: number | number[] | string | boolean): number {
    if (Array.isArray(value)) {
      return value[0]
    } else {
      if (typeof value === 'number') {
        return value
      } else if (typeof value === 'boolean') {
        return value ? 1.0 : 0.0
      } else {
        return parseFloat(value)
      }
    }
  }

  static fromOtherKwarg(otherKwarg: Kwarg): FloatKwarg {
    return new FloatKwarg(otherKwarg.name, this.prepare(otherKwarg.value))
  }
}

class ListOfFloatKwarg extends Kwarg {
  public value: number[] = $state([])

  constructor(name: string, value: number[]) {
    super(name, KwargType.LIST_OF_FLOAT)
    this.value = value
  }

  static prepare(value: number | number[] | string | boolean): number[] {
    if (Array.isArray(value)) {
      return value
    } else {
      return []
    }
  }

  static fromOtherKwarg(otherKwarg: Kwarg): ListOfFloatKwarg {
    return new ListOfFloatKwarg(otherKwarg.name, this.prepare(otherKwarg.value))
  }
}

class BooleanKwarg extends Kwarg {
  public value: boolean = $state(false)

  constructor(name: string, value: boolean) {
    super(name, KwargType.BOOLEAN)
    this.value = value
  }

  static prepare(value: number | number[] | string | boolean): boolean {
    if (typeof value === 'boolean') {
      return value
    } else if (Array.isArray(value)) {
      return value[0] === 1
    } else if (typeof value === 'number') {
      return value === 1
    } else {
      return value.toLowerCase() === 'true'
    }
  }

  static fromOtherKwarg(otherKwarg: Kwarg): BooleanKwarg {
    return new BooleanKwarg(otherKwarg.name, this.prepare(otherKwarg.value))
  }
}

class ReductionTypesKwarg extends Kwarg {
  static keyword = 'reduction'
  static default = 'mean'

  public value: string = $state(ReductionTypesKwarg.default)

  constructor(name: string, value: string) {
    super(name, KwargType.REDUCTION_TYPES)
    this.value = value
  }

  static prepare(value: number | number[] | string | boolean): string {
    if (typeof value === 'string') {
      return value
    } else {
      return ReductionTypesKwarg.default
    }
  }

  static fromOtherKwarg(otherKwarg: Kwarg): ReductionTypesKwarg {
    return new ReductionTypesKwarg(ReductionTypesKwarg.keyword, this.prepare(otherKwarg.value))
  }
}

const kwargMap = {
  [KwargType.FLOAT]: FloatKwarg,
  [KwargType.LIST_OF_FLOAT]: ListOfFloatKwarg,
  [KwargType.INTEGER]: IntKwarg,
  [KwargType.BOOLEAN]: BooleanKwarg,
  [KwargType.REDUCTION_TYPES]: ReductionTypesKwarg,
}

type KwargMap = {
  [K in KwargType]: InstanceType<(typeof kwargMap)[K]>
}

export {
  BooleanKwarg,
  FloatKwarg,
  IntKwarg,
  Kwarg,
  KwargError,
  type KwargMap,
  kwargMap,
  kwargNames,
  KwargType,
  ListOfFloatKwarg,
  ReductionTypesKwarg,
}
